import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Timer } from "three/addons/misc/Timer.js";
import GUI from "lil-gui";
import { Sky } from "three/examples/jsm/Addons.js";

/**
 * Loading Manager
 */
const loadingManager = new THREE.LoadingManager();
const loadingScreen = document.getElementById("loadingScreen");
const loadingBar = document.getElementById("loadingBar");
const loadingText = document.getElementById("loadingText");
const loadingPercentage = document.getElementById("loadingPercentage");

loadingManager.onStart = (url, itemsLoaded, itemsTotal) => {
  console.log(
    "Started loading file: " +
      url +
      ".\nLoaded " +
      itemsLoaded +
      " of " +
      itemsTotal +
      " files."
  );
  loadingText.textContent = "Loading textures...";
};

loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
  const progress = (itemsLoaded / itemsTotal) * 100;
  loadingBar.style.width = progress + "%";
  loadingPercentage.textContent = Math.round(progress) + "%";

  if (progress < 30) {
    loadingText.textContent = "Summoning spirits...";
  } else if (progress < 60) {
    loadingText.textContent = "Preparing haunted materials...";
  } else if (progress < 90) {
    loadingText.textContent = "Arranging the graveyard...";
  } else {
    loadingText.textContent = "Almost ready...";
  }

  console.log(
    "Loading progress: " +
      url +
      ". " +
      (itemsLoaded / itemsTotal) * 100 +
      "% loaded."
  );
};

loadingManager.onLoad = () => {
  console.log("Loading complete!");
  loadingBar.style.width = "100%";
  loadingPercentage.textContent = "100%";
  loadingText.textContent = "Welcome to Keith's Haunted House...";

  setTimeout(() => {
    loadingScreen.classList.add("fade-out");
    setTimeout(() => {
      loadingScreen.style.display = "none";
    }, 500);
  }, 500);
};

loadingManager.onError = (url) => {
  console.error("There was an error loading " + url);
  loadingText.textContent = "Error loading: " + url;
};

/**
 * Base
 */
// Debug
const gui = new GUI({title: "Limited Debug Panel"});
gui.close()

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader(loadingManager);

// Floor
const floorAlphaTexture = textureLoader.load("./floor/alpha.webp");
const floorColorTexture = textureLoader.load(
  "./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_diff_1k.webp"
);
floorColorTexture.colorSpace = THREE.SRGBColorSpace;
floorColorTexture.repeat.set(8, 8);
floorColorTexture.wrapS = THREE.RepeatWrapping;
floorColorTexture.wrapT = THREE.RepeatWrapping;
const floorARMTexture = textureLoader.load(
  "./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_arm_1k.webp"
);
floorARMTexture.repeat.set(8, 8);
floorARMTexture.wrapS = THREE.RepeatWrapping;
floorARMTexture.wrapT = THREE.RepeatWrapping;
const floorNormalTexture = textureLoader.load(
  "./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_nor_gl_1k.webp"
);
floorNormalTexture.repeat.set(8, 8);
floorNormalTexture.wrapS = THREE.RepeatWrapping;
floorNormalTexture.wrapT = THREE.RepeatWrapping;
const floorDisplacementTexture = textureLoader.load(
  "./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_disp_1k.webp"
);
floorDisplacementTexture.repeat.set(8, 8);
floorDisplacementTexture.wrapS = THREE.RepeatWrapping;
floorDisplacementTexture.wrapT = THREE.RepeatWrapping;

// Wall
const wallColorTexture = textureLoader.load(
  "./wall/weathered_brown_planks_1k/weathered_brown_planks_diff_1k.webp"
);
wallColorTexture.colorSpace = THREE.SRGBColorSpace;
wallColorTexture.repeat.set(1, 1.5);
// wallColorTexture.wrapS = THREE.RepeatWrapping;
wallColorTexture.wrapT = THREE.RepeatWrapping;
const wallARMTexture = textureLoader.load(
  "./wall/weathered_brown_planks_1k/weathered_brown_planks_arm_1k.webp"
);
const wallNormalTexture = textureLoader.load(
  "./wall/weathered_brown_planks_1k/weathered_brown_planks_nor_gl_1k.webp"
);

// Roof
const roofColorTexture = textureLoader.load(
  "./roof/roof_slates_02_1k/roof_slates_02_diff_1k.webp"
);
roofColorTexture.colorSpace = THREE.SRGBColorSpace;
const roofARMTexture = textureLoader.load(
  "./roof/roof_slates_02_1k/roof_slates_02_arm_1k.webp"
);
const roofNormalTexture = textureLoader.load(
  "./roof/roof_slates_02_1k/roof_slates_02_nor_gl_1k.webp"
);
roofColorTexture.repeat.set(3, 1);
roofARMTexture.repeat.set(3, 1);
roofNormalTexture.repeat.set(3, 1);
roofColorTexture.wrapS = THREE.RepeatWrapping;
roofARMTexture.wrapS = THREE.RepeatWrapping;
roofNormalTexture.wrapS = THREE.RepeatWrapping;

// Bush
const bushColorTexture = textureLoader.load(
  "./bush/leaves_forest_ground_1k/leaves_forest_ground_diff_1k.webp"
);
bushColorTexture.colorSpace = THREE.SRGBColorSpace;
const bushARMTexture = textureLoader.load(
  "./bush/leaves_forest_ground_1k/leaves_forest_ground_arm_1k.webp"
);
const bushNormalTexture = textureLoader.load(
  "./bush/leaves_forest_ground_1k/leaves_forest_ground_nor_gl_1k.webp"
);
bushColorTexture.repeat.set(2, 1);
bushARMTexture.repeat.set(2, 1);
bushNormalTexture.repeat.set(2, 1);
bushColorTexture.wrapS = THREE.RepeatWrapping;
bushARMTexture.wrapS = THREE.RepeatWrapping;
bushNormalTexture.wrapS = THREE.RepeatWrapping;

// Grave
const graveColorTexture = textureLoader.load(
  "./grave/plastered_stone_wall_1k/plastered_stone_wall_diff_1k.webp"
);
const graveARMTexture = textureLoader.load(
  "./grave/plastered_stone_wall_1k/plastered_stone_wall_arm_1k.webp"
);
const graveNormalTexture = textureLoader.load(
  "./grave/plastered_stone_wall_1k/plastered_stone_wall_nor_gl_1k.webp"
);

graveColorTexture.colorSpace = THREE.SRGBColorSpace;

// No need to change the wrapS and wrapT when the repeat values are less than 1.
graveColorTexture.repeat.set(0.3, 0.4);
graveARMTexture.repeat.set(0.3, 0.4);
graveNormalTexture.repeat.set(0.3, 0.4);

// Door
const doorColorTexture = textureLoader.load("./door/color.webp");
const doorAlphaTexture = textureLoader.load("./door/alpha.webp");
const doorAmbientOcclusionTexture = textureLoader.load(
  "./door/ambientOcclusion.webp"
);
const doorHeightTexture = textureLoader.load("./door/height.webp");
const doorNormalTexture = textureLoader.load("./door/normal.webp");
const doorMetalnessTexture = textureLoader.load("./door/metalness.webp");
const doorRoughnessTexture = textureLoader.load("./door/roughness.webp");

doorColorTexture.colorSpace = THREE.SRGBColorSpace;

/**
 * House
 */
const floorMeasurements = {
  width: 20,
  height: 20,
};

// for textures, AO/Rough/Metal combines the three into one file. Typically,
// Bump we won't use since its basically the same as noral map, but it is
// grayscale, so it is less detailed

// EXR is large file size with max data, PNG is medium file size with no
// compression artefacts, JPG is small file size with potential compression
// artefacts. For real time rendering we typically dont use EXR since its hard
// to load

// AO (ambient occlusion): Prevents the ambient light being applied to crevices

// Diffuse: The actual color

// Displacement: Will move the vertices up and down to create elevations

// Normal: Will fake the orientation to create details. DX and GL are different
// ways of orienting the normals and we need to go for GL.

// Rough: How smooth or rough the material is

// Normally we use JPG for textures and PNG for the Normal because we avoid
// lossy compression on normal maps to prevent visual artefacts. since we are
// using grungy textures, those artefacts won't be visible. Basically Diffuse is
// fine for JPG, but for AO/Rough/Metal, Displacement, and Normal we might want
// PNG. Displacement is also grayscale so not as bad to use jpg for it

const floor = new THREE.Mesh(
  new THREE.PlaneGeometry(
    floorMeasurements.width,
    floorMeasurements.height,
    100,
    100
  ),
  new THREE.MeshStandardMaterial({
    alphaMap: floorAlphaTexture,
    transparent: true,
    map: floorColorTexture,
    aoMap: floorARMTexture,
    roughnessMap: floorARMTexture,
    metalnessMap: floorARMTexture,
    normalMap: floorNormalTexture,
    // displacement map actually moves the vertices instead of faking it like
    // normal map, so we need the plane to have more subdivisions for this to
    // work
    displacementMap: floorDisplacementTexture,
    // turn down the scale to make it less extreme
    displacementScale: 0.3,
    // if you look at the texture file, there isnt really any blacks, so
    // everything is being pushed up. we can use displacement bias to fix this
    displacementBias: -0.2,
  })
);
floor.receiveShadow = true;
gui.add(floor.material, "transparent").onChange(() => {
  floor.material.needsUpdate = true;
});
gui
  .add(floor.material, "displacementScale")
  .min(0)
  .max(1)
  .step(0.001)
  .name("floorDisplacementScale");
gui
  .add(floor.material, "displacementBias")
  .min(-1)
  .max(1)
  .step(0.001)
  .name("floorDisplacementBias");
floor.rotation.x = -Math.PI * 0.5;
scene.add(floor);

// House Container
const wallsMeasurements = {
  width: 4,
  height: 2.5,
  depth: 4,
};
const house = new THREE.Group();
scene.add(house);

// Walls
const walls = new THREE.Mesh(
  new THREE.BoxGeometry(
    wallsMeasurements.width,
    wallsMeasurements.height,
    wallsMeasurements.depth
  ),
  new THREE.MeshStandardMaterial({
    map: wallColorTexture,
    aoMap: wallARMTexture,
    roughnessMap: wallARMTexture,
    metalnessMap: wallARMTexture,
    normalMap: wallNormalTexture,
  })
);
walls.position.y += wallsMeasurements.height / 2;
walls.castShadow = true;
walls.receiveShadow = true;
house.add(walls);

// Roof
const roof = new THREE.Mesh(
  new THREE.ConeGeometry(3.5, 1.5, 4),
  new THREE.MeshStandardMaterial({
    map: roofColorTexture,
    aoMap: roofARMTexture,
    roughnessMap: roofARMTexture,
    metalnessMap: roofARMTexture,
    normalMap: roofNormalTexture,
  })
);
roof.position.y = 2.5 + 1.5 / 2;
roof.rotation.y = Math.PI * 0.25;
roof.castShadow = true;
house.add(roof);

// Door
const door = new THREE.Mesh(
  new THREE.PlaneGeometry(2.2, 2.2, 100, 100),
  new THREE.MeshStandardMaterial({
    map: doorColorTexture,
    transparent: true,
    alphaMap: doorAlphaTexture,
    aoMap: doorAmbientOcclusionTexture,
    displacementMap: doorHeightTexture,
    displacementScale: 0.15,
    displacementBias: -0.04,
    normalMap: doorNormalTexture,
    metalnessMap: doorMetalnessTexture,
    roughnessMap: doorRoughnessTexture,
  })
);
// position.y + 1 instead of 1.1 because we are going to be cutting part of the
// texture off the top and bottom anyways.
door.position.y += 1;
door.position.z += 2 + 0.01;
house.add(door);

// Bushes
const bushGeometry = new THREE.SphereGeometry(1, 16, 16);
const bushMaterial = new THREE.MeshStandardMaterial({
  color: "#ccffcc",
  map: bushColorTexture,
  aoMap: bushARMTexture,
  roughnessMap: bushARMTexture,
  metalnessMap: bushARMTexture,
  normalMap: bushNormalTexture,
});

const bush1 = new THREE.Mesh(bushGeometry, bushMaterial);
bush1.scale.setScalar(0.5);
bush1.position.set(0.8, 0.2, 2.2);
bush1.rotation.x = -0.75;

const bush2 = new THREE.Mesh(bushGeometry, bushMaterial);
bush2.scale.setScalar(0.25);
bush2.position.set(1.4, 0.1, 2.1);
bush2.rotation.x = -0.75;

const bush3 = new THREE.Mesh(bushGeometry, bushMaterial);
bush3.scale.setScalar(0.4);
bush3.position.set(-0.8, 0.1, 2.2);
bush3.rotation.x = -0.75;

const bush4 = new THREE.Mesh(bushGeometry, bushMaterial);
bush4.scale.setScalar(0.15);
bush4.position.set(-1, 0.05, 2.6);
bush4.rotation.x = -0.75;

house.add(bush1, bush2, bush3, bush4);

// Graves
const graveGeometry = new THREE.BoxGeometry(0.6, 0.8, 0.2);
const graveMaterial = new THREE.MeshStandardMaterial({
  map: graveColorTexture,
  aoMap: graveARMTexture,
  roughnessMap: graveARMTexture,
  metalnessMap: graveARMTexture,
  normalMap: graveNormalTexture,
});

const graves = new THREE.Group();
scene.add(graves);

for (let i = 0; i < 30; i++) {
  const angle = Math.random() * Math.PI * 2;
  const x = Math.sin(angle);
  const z = Math.cos(angle);
  const radius = 3 + Math.random() * 4;
  const grave = new THREE.Mesh(graveGeometry, graveMaterial);
  grave.position.x = x * radius;
  grave.position.y = Math.random() * 0.4;
  grave.position.z = z * radius;
  grave.rotation.x = (Math.random() - 0.5) * 0.4;
  grave.rotation.y = (Math.random() - 0.5) * 0.4;
  grave.rotation.z = (Math.random() - 0.5) * 0.4;
  grave.castShadow = true;
  grave.receiveShadow = true;
  graves.add(grave);
}

const axesHelper = new THREE.AxesHelper(10);
scene.add(axesHelper);
/**
 * Lights
 *
 */
// Ambient light
const ambientLight = new THREE.AmbientLight("#86cdff", 0.275);
scene.add(ambientLight);

// Directional light
const directionalLight = new THREE.DirectionalLight("#86cdff", 1);
directionalLight.castShadow = true;
directionalLight.shadow.mapSize.set(256, 256);
directionalLight.shadow.camera.top = 8;
directionalLight.shadow.camera.bottom = -8;
directionalLight.shadow.camera.right = 8;
directionalLight.shadow.camera.left = -8;
directionalLight.shadow.camera.near = 1;
directionalLight.shadow.camera.far = 20;
directionalLight.position.set(3, 2, -8);
scene.add(new THREE.CameraHelper(directionalLight.shadow.camera));
scene.add(directionalLight);

const directionalLightHelper = new THREE.DirectionalLightHelper(
  directionalLight
);
scene.add(directionalLightHelper);

// Door Light
const doorLight = new THREE.PointLight("#ff7d46", 5);
doorLight.position.set(0, 2.2, 2.5);
house.add(doorLight);

/**
 * Ghosts
 */

const ghost1 = new THREE.PointLight("#8800ff", 6);
ghost1.shadow.mapSize.set(256, 256);
ghost1.shadow.camera.far = 10;
ghost1.castShadow = true;
const ghost2 = new THREE.PointLight("#ff0088", 6);
ghost2.shadow.mapSize.set(256, 256);
ghost2.shadow.camera.far = 10;
ghost2.castShadow = true;
const ghost3 = new THREE.PointLight("#ff0000", 6);
ghost3.shadow.mapSize.set(256, 256);
ghost3.shadow.camera.far = 10;
ghost3.castShadow = true;
scene.add(ghost1, ghost2, ghost3);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera

function setInitialZoom(camera, screenWidth) {
  // Define breakpoints and corresponding camera distances
  const breakpoints = [
    { width: 480, scale: 2 },   // Mobile phones
    { width: 768, scale: 2 },   // Tablets
    { width: 1024, scale: 1 },   // Small laptops
    { width: 1440, scale: 1 },   // Desktop
    { width: 1920, scale: 1 },   // Large desktop
    { width: Infinity, scale: 1 } // Ultra-wide screens
  ];
  
  // Find the appropriate distance based on screen width
  const scale = breakpoints.find(bp => screenWidth <= bp.width).scale;
  
  
  camera.position.set(
    4 * scale,  // x position scaled
    2 * scale,  // y position scaled  
    5 * scale   // z position scaled
  );
}
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
setInitialZoom(camera, window.innerWidth);
scene.add(camera);

// const camera2 = new THREE.OrthographicCamera(
// );
// camera2.position.x = 4;
// camera2.position.y = 2;
// camera2.position.z = 5;
// scene.add(camera2);
// scene.add(new THREE.CameraHelper(camera2))
// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.maxDistance = 20;
controls.maxPolarAngle = Math.PI / 2 - 0.1;
// controls.minAzimuthAngle = -Math.PI / 2;
// controls.maxAzimuthAngle = Math.PI / 2;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;

const sky = new Sky();
sky.material.uniforms["turbidity"].value = 10;
sky.material.uniforms["rayleigh"].value = 3;
sky.material.uniforms["mieCoefficient"].value = 0.1;
sky.material.uniforms["mieDirectionalG"].value = 0.95;
sky.material.uniforms["sunPosition"].value.set(0.3, -0.038, -0.95);
sky.scale.set(100, 100, 100);
scene.add(sky);

scene.fog = new THREE.FogExp2("#04343f", 0.1);

/**
 * Animate
 */
const timer = new Timer();

const tick = () => {
  // Timer
  timer.update();
  const elapsedTime = timer.getElapsed();
  const ghost1Angle = elapsedTime * 0.5;
  ghost1.position.set(
    Math.sin(ghost1Angle) * 4,
    Math.sin(ghost1Angle) *
      Math.sin(ghost1Angle * 2.34) *
      Math.sin(ghost1Angle * 3.45),
    Math.cos(ghost1Angle) * 4
  );

  const ghost2Angle = -elapsedTime * 0.38;
  ghost2.position.set(
    Math.sin(ghost2Angle) * 5,
    Math.sin(ghost2Angle) *
      Math.sin(ghost2Angle * 2.34) *
      Math.sin(ghost2Angle * 3.45),
    Math.cos(ghost2Angle) * 5
  );

  const ghost3Angle = elapsedTime * 0.23;
  ghost3.position.set(
    Math.sin(ghost3Angle) * 6,
    Math.sin(ghost3Angle) *
      Math.sin(ghost3Angle * 2.34) *
      Math.sin(ghost3Angle * 3.45),
    Math.cos(ghost3Angle) * 6
  );

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
